import { createApi } from "@reduxjs/toolkit/query/react";
import Axios from "axios";
import axiosBaseQuery from "../../util/axiosBaseQuery";
import { axiosNewPrivate } from "../../util/axios";

export interface CreateIdeaSubmissionPayload {
    text: string;
    /** Optional — the visitor may leave it blank. */
    contactEmail?: string;
}

export interface CreateIdeaSubmissionResponse {
    id: string;
}

/**
 * "More to come" ideas form — the write side of the card #2588 added.
 *
 * Lives on the new backend (`/app/idea-submissions`), so the calls go through
 * `axiosNewPrivate`, which carries the X-Api-Key / X-Tenant headers those
 * routes expect and attaches the account session token when one exists. The
 * slice's own base query is the old backend, hence `queryFn` rather than
 * `query` — same shape as `updateIssueReportStatus`.
 */
export const ideaSubmissionApiSlice = createApi({
    reducerPath: "ideaSubmissionApiSlice",
    baseQuery: axiosBaseQuery(),
    // One-way submit with no list to refresh, so this slice declares no tags.
    tagTypes: [],
    endpoints: (builder) => ({
        createIdeaSubmission: builder.mutation<
            CreateIdeaSubmissionResponse,
            CreateIdeaSubmissionPayload
        >({
            queryFn: async ({ text, contactEmail }) => {
                try {
                    const result = await axiosNewPrivate.post("idea-submissions", {
                        text,
                        // Omit rather than send an empty string: the backend
                        // treats "" as absent anyway, and this keeps the wire
                        // payload honest about what the visitor supplied.
                        ...(contactEmail && contactEmail.trim().length > 0
                            ? { contactEmail: contactEmail.trim() }
                            : {}),
                    });
                    return { data: result.data as CreateIdeaSubmissionResponse };
                } catch (err) {
                    // The error must reach the component distinguishably — a
                    // failed submit that renders as "nothing happened" is the
                    // #2218/#2221 failure (a catch that reported success hid a
                    // six-day outage).
                    if (!Axios.isAxiosError(err)) {
                        return { error: { status: undefined, data: String(err) } };
                    }
                    return {
                        error: {
                            status: err.response?.status,
                            data: err.response?.data ?? err.message,
                        },
                    };
                }
            },
        }),
    }),
});

export const { useCreateIdeaSubmissionMutation } = ideaSubmissionApiSlice;
